import React from 'react';
import type { Test } from '../../types';

const sleepBuckets = [
  { label: 'Low (<4hrs)', min: 0, max: 4 },
  { label: 'Moderate (4-8hrs)', min: 4, max: 8 },
  { label: 'High (>8hrs)', min: 8, max: Number.POSITIVE_INFINITY },
];

const rtBuckets = [
  { label: 'Low (<295ms)', min: 0, max: 295 },
  { label: 'Moderate (295-500ms)', min: 295, max: 500 },
  { label: 'High (>500ms)', min: 500, max: Number.POSITIVE_INFINITY },
];

const getBucketIndex = (value: number | undefined, buckets: Array<{ min: number; max: number }>) => {
  if (value == null || Number.isNaN(value)) return -1;
  return buckets.findIndex((bucket) => value >= bucket.min && value < bucket.max);
};

const GOLD_RGB = '217, 169, 59';

const toColor = (value: number, maxValue: number) => {
  if (maxValue <= 0) return `rgba(${GOLD_RGB}, 0.08)`;
  const normalized = Math.min(1, value / maxValue);
  const alpha = 0.12 + normalized * 0.75;
  return `rgba(${GOLD_RGB}, ${alpha.toFixed(2)})`;
};

interface AnalyticsHeatmapProps {
  tests: Test[];
}

export const AnalyticsHeatmap: React.FC<AnalyticsHeatmapProps> = ({ tests }) => {
  const matrix = sleepBuckets.map(() => rtBuckets.map(() => 0));

  tests.forEach((test) => {
    if (!test.results) return;
    const sleepIndex = getBucketIndex(test.lastSleepDuration, sleepBuckets);
    const rtIndex = getBucketIndex(test.results.averageReactionTime, rtBuckets);
    if (sleepIndex === -1 || rtIndex === -1) return;
    matrix[sleepIndex][rtIndex] += 1;
  });

  const maxValue = Math.max(0, ...matrix.flat());

  return (
    <div className="bg-navy-800 rounded-2xl p-6 shadow-sm border border-navy-600">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-white">Average RT vs Sleep Duration</h2>
      </div>

      <div className="grid grid-cols-[80px_160px_1fr_48px] gap-4 items-stretch">
        <div className="flex items-center justify-center h-[17rem]">
          <span className="text-sm font-semibold text-navy-300 rotate-[-90deg] whitespace-nowrap">Sleep Duration</span>
        </div>

        <div className="flex flex-col justify-between text-sm text-navy-200 font-medium">
          {sleepBuckets.map((bucket) => (
            <div key={bucket.label} className="h-20 flex items-center">
              {bucket.label}
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {matrix.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="grid grid-cols-3 gap-4">
              {row.map((value, colIndex) => (
                <div
                  key={`cell-${rowIndex}-${colIndex}`}
                  className="h-20 rounded-2xl flex items-center justify-center text-lg font-semibold text-white shadow-sm"
                  style={{ backgroundColor: toColor(value, maxValue) }}
                >
                  {value}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center text-xs text-navy-300 justify-center h-[17rem]">
          <span className="mb-2">{maxValue}</span>
          <div
            className="w-2 h-64 rounded-full"
            style={{ background: `linear-gradient(180deg, rgba(${GOLD_RGB},0.9), rgba(${GOLD_RGB},0.12))` }}
          ></div>
          <span className="mt-2">0</span>
        </div>
      </div>

      <div className="grid grid-cols-[80px_160px_1fr_48px] mt-4">
        <div></div>
        <div></div>
        <div className="grid grid-cols-3 text-center text-sm text-navy-200 font-medium">
          {rtBuckets.map((bucket) => (
            <div key={bucket.label}>{bucket.label}</div>
          ))}
        </div>
        <div></div>
      </div>

      <div className="grid grid-cols-[80px_160px_1fr_48px] mt-3">
        <div></div>
        <div></div>
        <div className="text-center text-sm text-navy-300 font-medium">Average RT</div>
        <div></div>
      </div>
    </div>
  );
};
